import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/icons";

export type ColumnMeta = {
  id: string;
  label: string;
  /** Required columns cannot be hidden. */
  required?: boolean;
};

type Props = {
  columns: readonly ColumnMeta[];
  hidden: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onReset?: () => void;
  disabled?: boolean;
};

export function TableColumnsMenu({ columns, hidden, onToggle, onReset, disabled = false }: Props) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(event: MouseEvent) {
      if (!wrapperRef.current) return;
      if (event.target instanceof Node && wrapperRef.current.contains(event.target)) return;
      setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const visibleCount = columns.length - hidden.size;

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Columns"
        className={[
          "flex shrink-0 items-center gap-1 rounded-[4px] border p-2 text-small font-medium shadow-flat",
          disabled
            ? "cursor-not-allowed border-grey-200 bg-white text-grey-300"
            : open
              ? "border-grey-500 bg-grey-100 text-black"
              : "border-grey-200 bg-white text-black hover:bg-grey-50",
        ].join(" ")}
      >
        <Icon name="view_column" size={20} weight={300} />
        {/* Label collapses to an icon-only control on phones so it stops
            crowding the breadcrumb / heading in the table chrome row; the
            aria-label above keeps it announced. */}
        <span className="hidden sm:inline">Columns</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Column settings"
          className="absolute right-0 top-full z-20 mt-1 flex w-[260px] flex-col gap-2 rounded-[4px] border border-grey-200 bg-white p-2 shadow-flat"
        >
          <div className="flex items-center justify-between px-2 pt-1">
            <span className="text-tiny font-medium text-grey-900">
              {visibleCount} of {columns.length} visible
            </span>
            {onReset && (
              <button
                type="button"
                onClick={onReset}
                className="text-tiny font-medium text-green-700 hover:underline"
              >
                Reset
              </button>
            )}
          </div>
          <ul className="flex flex-col">
            {columns.map((column) => {
              const isHidden = hidden.has(column.id);
              const disabled = column.required;
              return (
                <li key={column.id}>
                  <button
                    type="button"
                    onClick={() => {
                      if (disabled) return;
                      onToggle(column.id);
                    }}
                    disabled={disabled}
                    className={[
                      "flex w-full items-center gap-2 rounded-[4px] px-2 py-1.5 text-left text-small font-medium",
                      disabled ? "cursor-not-allowed text-grey-500" : "text-black hover:bg-grey-50",
                    ].join(" ")}
                  >
                    <Icon
                      name={isHidden ? "check_box_outline_blank" : "check_box"}
                      size={20}
                      className={isHidden ? "text-grey-300" : "text-green-500"}
                    />
                    <span className="flex-1">{column.label}</span>
                    {disabled && <span className="text-tiny text-grey-500">Required</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
